import { memo, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { ESTADO_NAMES, humanize, isEmpty, STATUS_EXPLANATIONS, type Filters, type PropertySort } from '../lib/filters';
import { SortFilter } from './FilterBar';
import type { PropertyListItem, GradoRezago } from '../lib/types';
import { oppTier } from '../lib/opportunity';
import { EmptyState } from './EmptyState';
import { ResultsListSkeleton } from './Skeletons';
import { WatchStar } from './WatchStar';
import { gradoToColor } from './SocioLayer';
import { useAuth } from '../lib/auth';
import { openLogin } from '../lib/signup';
import type { ListMode } from './ResultsToolbar';
import type { WatchlistItem } from '../lib/types';

interface Props {
  filters: Filters;
  /** Qué muestra el panel: los resultados de la búsqueda o mis observados. */
  mode: ListMode;
  selectedId: string | null;
  /** Propiedad bajo el cursor (en lista o mapa) — resalta la card par. */
  hoveredId?: string | null;
  onSelect: (id: string) => void;
  /** Notifica hover de cards para resaltar el marker correspondiente. */
  onHover?: (id: string | null) => void;
  /** Permite "limpiar filtros" desde el empty state. */
  onClearFilters: () => void;
  /** Cambia el orden del listado (va en la cabecera del panel). */
  onSortChange: (sort: PropertySort | undefined) => void;
  onClose: () => void;
}

const PAGE_SIZE = 25;

/**
 * Panel de la lista, debajo de la barra de filtros (misma columna que
 * flota sobre el mapa, así nunca queda tapado por ella). Se monta sólo
 * abierto; las acciones (ver lista, observados, copiar enlace, exportar)
 * viven en la barra — ver ResultsToolbar.
 *
 * Cards compactas con foto + precio + dirección + tipo. Click → drawer.
 * Paginación con "Cargar más".
 *
 * Usa /v1/properties (no GeoJSON) para incluir también las que NO
 * tienen coords todavía — para que el usuario pueda navegar el catálogo
 * mientras el geocoder progresa.
 */
export function ResultsList({ filters, mode, selectedId, hoveredId, onSelect, onHover, onClearFilters, onSortChange, onClose }: Props) {
  const { user, watchItems } = useAuth();
  const showWatch = mode === 'watch';
  const [items, setItems] = useState<PropertyListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cursor de paginación DEL SERVIDOR (r.nextOffset), no offset calculado
  // en cliente: si el server clampa el limit, el offset local saltaba o
  // duplicaba filas. null = no hay más páginas.
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  // Generación de la lista: se bumpea en cada reset por cambio de filtros.
  // Un loadMore en vuelo de una generación vieja NO puede aplicar su
  // respuesta (antes mezclaba filas de filtros anteriores con keys
  // duplicadas en el grid).
  const listGenRef = useRef(0);

  // Re-fetch desde 0 cuando cambian filtros. Sin sesión no se pide nada:
  // la lista es material cerrado (RFC-0021 Bloque C) y pedirla solo
  // produciría 401 en consola.
  useEffect(() => {
    if (showWatch || !user) return;
    const gen = ++listGenRef.current;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setNextOffset(null);
    api
      .propertiesList({ filters, limit: PAGE_SIZE, offset: 0 }, ctrl.signal)
      .then((r) => {
        if (gen !== listGenRef.current) return;
        setItems(r.items);
        setNextOffset(r.nextOffset);
        setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted || gen !== listGenRef.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [filters, showWatch, user]);

  function loadMore() {
    if (nextOffset === null || loading) return;
    const gen = listGenRef.current;
    setLoading(true);
    api
      .propertiesList({ filters, limit: PAGE_SIZE, offset: nextOffset })
      .then((r) => {
        // Si los filtros cambiaron mientras esta página volaba, se descarta.
        if (gen !== listGenRef.current) return;
        setItems((prev) => [...prev, ...r.items]);
        setNextOffset(r.nextOffset);
        setLoading(false);
      })
      .catch(() => {
        if (gen !== listGenRef.current) return;
        setLoading(false);
      });
  }
  const hasMore = nextOffset !== null;

  const filtersActive = !isEmpty(filters);

  // Cuando el hover viene del mapa, acercamos la card par a la vista.
  // `block: 'nearest'` no mueve nada si ya es visible (hover local = no-op).
  // CSS.escape: el id viene del API — un carácter raro (comilla) tiraba
  // querySelector con SyntaxError.
  useEffect(() => {
    if (!hoveredId) return;
    document
      .querySelector(`[data-prop-id="${CSS.escape(hoveredId)}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [hoveredId]);

  const count = showWatch ? watchItems.length : items.length;

  return (
    <section className="results-list open" aria-label={showWatch ? 'Mis observados' : 'Lista de propiedades'}>
      <div className="results-head">
        <span className="results-title">{showWatch ? '★ Mis observados' : 'Propiedades'}</span>
        {user && count > 0 && (
          <span className="results-count">
            {showWatch ? count : `${count}${hasMore ? '+' : ''}`}
          </span>
        )}
        {!showWatch && user && <SortFilter value={filters.sort} onChange={onSortChange} />}
        <button type="button" className="results-close" onClick={onClose} aria-label="Cerrar lista" title="Cerrar lista">×</button>
      </div>

      {showWatch && (
        <div className="results-body">
          {watchItems.length === 0 ? (
            <EmptyState
              icon="☆"
              title="Aún no observas ninguna propiedad"
              hint="Usa la estrella ★ en una tarjeta o en la ficha para guardarla aquí."
            />
          ) : (
            <div className="results-grid">
              {watchItems.map((w) => (
                <WatchCard
                  key={w.id}
                  item={w}
                  selected={w.id === selectedId}
                  onClick={() => onSelect(w.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {!showWatch && !user && (
        <div className="results-body">
          <EmptyState
            icon="🔒"
            title="El listado requiere una cuenta"
            hint="La lista, la ficha de cada inmueble y su ubicación son para cuentas de TerraKairos. Las cuentas las abre TerraKairos por acuerdo."
            actionLabel="Iniciar sesión"
            onAction={openLogin}
          />
        </div>
      )}

      {!showWatch && user && (
        <div className="results-body">
          {error ? (
            <EmptyState
              variant="error"
              icon="!"
              title="No pudimos traer la lista"
              hint={error}
              actionLabel="Reintentar"
              onAction={() => {
                // Reusa el fetch del efecto de reset (misma generación,
                // mismo abort) en vez de un tercer fetch duplicado a mano.
                const gen = ++listGenRef.current;
                setError(null);
                setLoading(true);
                api.propertiesList({ filters, limit: PAGE_SIZE, offset: 0 })
                  .then((r) => {
                    if (gen !== listGenRef.current) return;
                    setItems(r.items);
                    setNextOffset(r.nextOffset);
                    setLoading(false);
                  })
                  .catch((e) => {
                    if (gen !== listGenRef.current) return;
                    setError(e instanceof Error ? e.message : String(e));
                    setLoading(false);
                  });
              }}
            />
          ) : loading && items.length === 0 ? (
            <ResultsListSkeleton count={6} />
          ) : items.length === 0 ? (
            <EmptyState
              icon="∅"
              title={filtersActive ? 'Sin resultados con estos filtros' : 'Aún no hay propiedades'}
              hint={
                filtersActive
                  ? 'Prueba a quitar el estado, el rango de precio o limpia todos los filtros.'
                  : 'El catálogo se actualizará pronto.'
              }
              actionLabel={filtersActive ? 'Limpiar filtros' : undefined}
              onAction={filtersActive ? onClearFilters : undefined}
            />
          ) : (
            <>
              <div className="results-grid">
                {items.map((p) => (
                  <div className="property-card-wrap" key={p.id}>
                    <PropertyCard
                      item={p}
                      selected={p.id === selectedId}
                      hovered={p.id === hoveredId}
                      onClick={() => onSelect(p.id)}
                      onHover={(on) => onHover?.(on ? p.id : null)}
                    />
                    <WatchStar propertyId={p.id} />
                  </div>
                ))}
              </div>
              {hasMore && (
                <button className="load-more" onClick={loadMore} disabled={loading}>
                  {loading ? 'Cargando…' : 'Cargar más'}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Card memoizada con comparador propio: `hoveredId` vive en App, así que
 * cada hover re-renderizaba TODAS las cards cargadas. Los handlers son
 * closures nuevos por render pero solo capturan `item.id` — comparar
 * item/selected/hovered es suficiente y seguro.
 */
const PropertyCard = memo(function PropertyCard({ item, selected, hovered, onClick, onHover }: {
  item: PropertyListItem;
  selected: boolean;
  /** True cuando el cursor está sobre el marker par en el mapa. */
  hovered: boolean;
  onClick: () => void;
  onHover: (on: boolean) => void;
}) {
  const price = item.asking_price !== null && item.asking_price > 1
    ? formatMoney(item.asking_price)
    : 'sin precio';
  const surface = item.surface_built_m2 ? `${Number(item.surface_built_m2).toLocaleString('es-MX')} m²` : null;
  const beds = item.bedrooms !== null ? `${item.bedrooms} rec` : null;
  const pricePerM2 = item.opp_price_m2 !== null && item.opp_price_m2 > 0
    ? `${formatMoney(item.opp_price_m2)}/m²`
    : null;
  const subline = [surface, beds, pricePerM2].filter(Boolean).join(' · ');

  return (
    <button
      className={`property-card ${selected ? 'selected' : ''} ${hovered ? 'hovered' : ''}`}
      data-prop-id={item.id}
      onClick={onClick}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <div className="card-photo">
        {item.photo_url ? (
          <img
            src={item.photo_url}
            alt={`${humanize('propertyType', item.property_type)}${item.municipio ? ' en ' + item.municipio : ''}`}
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div className="card-no-photo">{humanize('propertyType', item.property_type)}</div>
        )}
      </div>
      <div className="card-body">
        <div className="card-price">
          {price}
          {isRecentPriceDrop(item) && (
            <span
              className="discount-pill price-drop-pill"
              title={`Bajó ${Math.abs(item.last_price_change_pct ?? 0).toFixed(1)}% el ${formatChipDate(item.last_price_change_at)} — señal fuerte de urgencia del vendedor`}
            >
              ↓ {Math.abs(item.last_price_change_pct ?? 0).toFixed(0)}% precio
            </span>
          )}
          {item.discount_pct !== null && item.discount_pct > 0 && (
            <span className="discount-pill">-{Number(item.discount_pct).toFixed(0)}%</span>
          )}
          {item.opp_discount_vs_avaluo_pct !== null && item.opp_discount_vs_avaluo_pct >= 15 && (
            <span
              className="discount-pill discount-avaluo"
              title={`Descuento real vs avalúo: ${item.opp_discount_vs_avaluo_pct.toFixed(0)}%`}
            >
              -{item.opp_discount_vs_avaluo_pct.toFixed(0)}% vs avalúo
            </span>
          )}
          {item.opportunity_score !== null && (
            <OpportunityBadge score={item.opportunity_score} />
          )}
        </div>
        <div className="card-addr" title={item.address_normalized ?? ''}>
          {item.address_normalized ?? '(sin dirección)'}
        </div>
        <div className="card-meta">
          <span>{humanize('propertyType', item.property_type)}</span>
          {item.cve_ent && <span> · {ESTADO_NAMES[item.cve_ent] ?? item.cve_ent}</span>}
          {item.municipio && <span> · {item.municipio}</span>}
          {item.opp_n_photos !== null && item.opp_n_photos > 1 && (
            <span className="card-meta-photos"> · {item.opp_n_photos} fotos</span>
          )}
        </div>
        {(item.opp_has_litigio ||
          item.opp_is_libre_gravamen ||
          item.cenapred_seismic_zone ||
          item.cenapred_flood_risk ||
          item.zona_riesgo_flag ||
          item.acquisition_status === 'CANDIDATE_ONLY' ||
          item.acquisition_status === 'DELISTED_UNCONFIRMED' ||
          (item.shf_yoy_pct !== null && Math.abs(item.shf_yoy_pct) >= 4) ||
          item.socio_grado ||
          (item.opp_crime_yoy_pct !== null && Math.abs(item.opp_crime_yoy_pct) >= 5)) && (
          <div className="card-flags">
            {item.socio_grado && (
              <SocioChip grado={item.socio_grado} precision={item.geocode_precision} />
            )}
            {/* RFC-0021 A7: el 79% del inventario es CANDIDATE_ONLY y la
                etiqueta sola no explica qué puede hacer el usuario. */}
            {(item.acquisition_status === 'CANDIDATE_ONLY' ||
              item.acquisition_status === 'DELISTED_UNCONFIRMED') && (
              <span className="flag" title={STATUS_EXPLANATIONS[item.acquisition_status]}>
                ◌ {humanize('status', item.acquisition_status)}
              </span>
            )}
            {item.opp_has_litigio && (
              <span className="flag flag-warn" title="Litigio pendiente reportado">
                ⚠ Litigio
              </span>
            )}
            {item.opp_is_libre_gravamen && (
              <span className="flag flag-good" title="Listing declara libre de gravamen">
                ✓ Libre gravamen
              </span>
            )}
            {item.opp_crime_yoy_pct !== null && Math.abs(item.opp_crime_yoy_pct) >= 5 && (
              <CrimeChip yoyPct={item.opp_crime_yoy_pct} />
            )}
            {item.shf_yoy_pct !== null && Math.abs(item.shf_yoy_pct) >= 4 && (
              <ShfChip yoyPct={item.shf_yoy_pct} level={item.shf_level} />
            )}
            {item.cenapred_seismic_zone && (
              <span className="flag flag-info" title="Zona sísmica NTC/CFE (CENAPRED)">
                Sísmica {item.cenapred_seismic_zone}
              </span>
            )}
            {item.cenapred_flood_risk && (
              <span className="flag flag-info" title="Riesgo de inundación municipal (CENAPRED)">
                Inund. {shortFloodLabel(item.cenapred_flood_risk)}
              </span>
            )}
            {item.zona_riesgo_flag && (
              <span className="flag flag-risk" title="Zona D sísmica o inundación Alto/Muy alto">
                ⚠ Zona riesgo
              </span>
            )}
          </div>
        )}
        {subline && <div className="card-subline">{subline}</div>}
      </div>
    </button>
  );
}, (prev, next) =>
  prev.item === next.item && prev.selected === next.selected && prev.hovered === next.hovered);

/**
 * Card compacta para "Mis observados": resumen + nota + estrella para
 * quitar. Shape distinto a PropertyListItem (viene de /v1/watchlist).
 */
function WatchCard({ item, selected, onClick }: {
  item: WatchlistItem;
  selected: boolean;
  onClick: () => void;
}) {
  const priceNum = item.asking_price !== null ? Number(item.asking_price) : null;
  const price = priceNum !== null && priceNum > 1 ? formatMoney(priceNum) : 'sin precio';
  return (
    <div className={`property-card-wrap`}>
      <button
        className={`property-card watch-card ${selected ? 'selected' : ''}`}
        data-prop-id={item.id}
        onClick={onClick}
      >
        <div className="card-body">
          <div className="card-price">
            {price}
            {item.opportunity_score !== null && <OpportunityBadge score={item.opportunity_score} />}
          </div>
          <div className="card-addr" title={item.address_normalized ?? ''}>
            {item.address_normalized ?? '(sin dirección)'}
          </div>
          <div className="card-meta">
            <span>{humanize('propertyType', item.property_type ?? '')}</span>
            {item.cve_ent && <span> · {ESTADO_NAMES[item.cve_ent] ?? item.cve_ent}</span>}
            {item.municipio && <span> · {item.municipio}</span>}
          </div>
          {item.note && <div className="watch-note">“{item.note}”</div>}
        </div>
      </button>
      <WatchStar propertyId={item.id} />
    </div>
  );
}

function OpportunityBadge({ score }: { score: number }) {
  const rounded = Math.round(score);
  const tier = oppTier(score);
  return (
    <span
      className={`opp-badge opp-${tier}`}
      title={`Opportunity Score ${score.toFixed(1)}/100 — combina precio vs zona, crimen YoY, completitud, legal y liquidez`}
    >
      {rounded}
    </span>
  );
}

/**
 * Chip de tendencia de criminalidad municipal (YoY del SESNSP). Convención
 * del backend: > 0 = subió (malo, rojo), < 0 = bajó (bueno, verde).
 */
function CrimeChip({ yoyPct }: { yoyPct: number }) {
  const up = yoyPct > 0;
  const cls = up ? 'flag-warn' : 'flag-good';
  const arrow = up ? '▲' : '▼';
  return (
    <span
      className={`flag ${cls}`}
      title={`Delitos municipales ${up ? 'al alza' : 'a la baja'} ${yoyPct.toFixed(0)}% interanual (SESNSP)`}
    >
      {arrow} {Math.abs(yoyPct).toFixed(0)}% crimen
    </span>
  );
}

/**
 * Chip de plusvalía de mercado (SHF). Convención: > 0 = la zona se aprecia
 * (bueno para revalorización, verde), < 0 = se deprecia (rojo). Es señal de
 * tendencia zonal (índice base 2017=100), no un avalúo individual.
 */
function ShfChip({ yoyPct, level }: { yoyPct: number; level: PropertyListItem['shf_level'] }) {
  const up = yoyPct >= 0;
  const cls = up ? 'flag-good' : 'flag-warn';
  const arrow = up ? '▲' : '▼';
  const scope = level === 'municipio' ? 'municipal' : level === 'estado' ? 'estatal' : 'nacional';
  return (
    <span
      className={`flag ${cls}`}
      title={`Plusvalía ${scope} ${up ? '+' : ''}${yoyPct.toFixed(1)}% interanual (SHF, índice de precios)`}
    >
      {arrow} {Math.abs(yoyPct).toFixed(0)}% plusvalía
    </span>
  );
}

/**
 * Chip de nivel socioeconómico (RFC-0022 Fase 2): grado de rezago social
 * CONEVAL de la AGEB. La escala va INVERTIDA respecto al NSE — "Muy bajo"
 * rezago = nivel alto — así que el chip muestra el nivel traducido y deja el
 * grado en el tooltip. Sólo llega con precisión STREET/COLONIA: el API no
 * lo asigna a centroides municipales, y el tooltip dice de qué nivel de
 * precisión viene para que nadie lo lea como dato del predio.
 */
function SocioChip({ grado, precision }: { grado: GradoRezago; precision: PropertyListItem['geocode_precision'] }) {
  const color = gradoToColor(grado);
  const scope = precision === 'STREET' ? 'de la dirección' : 'de la colonia';
  return (
    <span
      className="flag flag-socio"
      style={{ color, borderColor: color }}
      title={`Nivel socioeconómico ${NIVEL_SOCIO[grado]} — rezago social "${grado}" (CONEVAL 2020, AGEB ${scope})`}
    >
      ◉ Nivel {NIVEL_SOCIO[grado]}
    </span>
  );
}

const NIVEL_SOCIO: Record<GradoRezago, string> = {
  'Muy bajo': 'alto',
  'Bajo': 'medio-alto',
  'Medio': 'medio',
  'Alto': 'medio-bajo',
  'Muy alto': 'bajo',
};

/**
 * Chip ↓precio: solo recortes reales (pct negativo) y recientes (≤60
 * días) — un recorte de hace meses ya está "absorbido" por el mercado.
 */
const PRICE_DROP_WINDOW_MS = 60 * 24 * 60 * 60 * 1000;

function isRecentPriceDrop(item: PropertyListItem): boolean {
  if (item.last_price_change_pct === null || item.last_price_change_pct >= -0.5) return false;
  if (!item.last_price_change_at) return false;
  const t = Date.parse(item.last_price_change_at);
  return Number.isFinite(t) && Date.now() - t <= PRICE_DROP_WINDOW_MS;
}

function formatChipDate(iso: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function shortFloodLabel(grado: string): string {
  const g = grado.trim().toLowerCase();
  if (g === 'muy bajo') return 'Muy bajo';
  if (g === 'muy alto') return 'Muy alto';
  return grado.length > 12 ? grado.slice(0, 10) + '…' : grado;
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n);
}
